import { safeErr } from '../App';
import React, { useState, useEffect } from 'react';
import { useAuth, authApi } from '../auth';

const ROLES = [
  { id:'admin',     label:'Admin',     desc:'Full access including settings and user management', color:'var(--blue)',   bg:'var(--blue-bg)'   },
  { id:'manager',   label:'Manager',   desc:'Leads, email, pipeline, analytics — no settings',   color:'var(--green)',  bg:'var(--green-bg)'  },
  { id:'sales_rep', label:'Sales Rep', desc:'Leads, contacts, email, replies, pipeline, campaigns', color:'var(--amber)', bg:'var(--amber-bg)'  },
  { id:'read_only', label:'Read Only', desc:'Analytics dashboard only — cannot send emails',      color:'var(--purple)', bg:'var(--purple-bg)' },
];

const PERM_ICONS = {
  leads:'◎', contacts:'👥', email:'✉', replies:'↩',
  campaigns:'🚀', pipeline:'▦', analytics:'📊', settings:'⚙', users:'👤', generate_leads:'✦',
};

const ROLE_PERMS = {
  admin:     ['leads','contacts','email','replies','campaigns','pipeline','analytics','settings','users','generate_leads'],
  manager:   ['leads','contacts','email','replies','campaigns','pipeline','analytics','generate_leads'],
  sales_rep: ['leads','contacts','email','replies','campaigns','pipeline','generate_leads'],
  read_only: ['analytics'],
};

function RoleBadge({ role }) {
  const r = ROLES.find(x=>x.id===role) || ROLES[2];
  return (
    <span style={{padding:'2px 9px',borderRadius:20,fontSize:11,fontWeight:600,
      background:r.bg,color:r.color,border:`1px solid ${r.color}22`}}>
      {r.label}
    </span>
  );
}

function CreateUserModal({ onClose, onCreated, showToast }) {
  const [f, setF]         = useState({name:'',email:'',password:'',role:'sales_rep'});
  const [saving, setSave] = useState(false);
  const [err,    setErr]  = useState('');
  const set = (k,v) => setF(p=>({...p,[k]:v}));

  const save = async () => {
    if (!f.name||!f.email||!f.password) { setErr('All fields required'); return; }
    if (f.password.length < 6) { setErr('Password must be at least 6 characters'); return; }
    setSave(true); setErr('');
    try {
      await authApi.post('/users', f);
      showToast(`✓ ${f.name} created as ${f.role}`);
      onCreated();
    } catch(e) { setErr(safeErr(e, 'Failed to create user')); }
    finally { setSave(false); }
  };

  const selectedRole = ROLES.find(r=>r.id===f.role);

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal-box" style={{width:500}}>
        <div className="modal-header">
          <span className="modal-title">+ Create new user</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {err && <div className="alert alert-error">{err}</div>}

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Full name</label>
              <input className="form-input" value={f.name} onChange={e=>set('name',e.target.value)} placeholder="Michael D"/>
            </div>
            <div className="form-group">
              <label className="form-label">Email address</label>
              <input className="form-input" type="email" value={f.email} onChange={e=>set('email',e.target.value)} placeholder="michael@pickzy.com"/>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Password</label>
            <input className="form-input" type="password" value={f.password}
              onChange={e=>set('password',e.target.value)} placeholder="Min. 6 characters"/>
          </div>

          <div className="form-group">
            <label className="form-label">Role</label>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginTop:4}}>
              {ROLES.map(r=>(
                <div key={r.id} onClick={()=>set('role',r.id)} style={{
                  padding:'10px 12px', borderRadius:'var(--r)', cursor:'pointer',
                  border:`2px solid ${f.role===r.id?r.color:'var(--border)'}`,
                  background:f.role===r.id?r.bg:'var(--surface)',
                  transition:'all .12s',
                }}>
                  <div style={{fontWeight:600,fontSize:13,color:f.role===r.id?r.color:'var(--text)'}}>{r.label}</div>
                  <div style={{fontSize:11,color:'var(--text-2)',marginTop:2,lineHeight:1.4}}>{r.desc}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Permissions preview */}
          {selectedRole && (
            <div style={{background:'var(--surface2)',borderRadius:'var(--r)',padding:'10px 12px',marginTop:4}}>
              <div style={{fontSize:11,fontWeight:600,color:'var(--text-2)',marginBottom:6,textTransform:'uppercase',letterSpacing:'.05em'}}>
                {selectedRole.label} can access:
              </div>
              <div style={{display:'flex',flexWrap:'wrap',gap:5}}>
                {['leads','contacts','email','replies','campaigns','pipeline','analytics','settings','users','generate_leads']
                  .map(p => {
                    const has = (ROLE_PERMS[f.role]||[]).includes(p);
                    return (
                      <span key={p} style={{
                        padding:'2px 8px', borderRadius:20, fontSize:11,
                        background: has?'var(--green-bg)':'var(--surface)',
                        color:      has?'var(--green)':'var(--text-3)',
                        border:    `1px solid ${has?'var(--green-border)':'var(--border)'}`,
                        textDecoration: has?'none':'line-through',
                      }}>
                        {PERM_ICONS[p]} {p.replace('_',' ')}
                      </span>
                    );
                  })}
              </div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-primary" disabled={saving} onClick={save}>
            {saving?'Creating…':'Create user'}
          </button>
          <button className="btn" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default function Users({ showToast }) {
  const { user: me } = useAuth();
  const [users,     setUsers]   = useState([]);
  const [loading,   setLoad]    = useState(true);
  const [showCreate,setCreate]  = useState(false);
  const [editUser,  setEdit]    = useState(null);
  const [resetPw,   setResetPw] = useState(null);
  const [newPw,     setNewPw]   = useState('');

  const load = () => {
    setLoad(true);
    authApi.get('/users').then(r=>setUsers(r.data)).finally(()=>setLoad(false));
  };
  useEffect(()=>{ load(); },[]);

  const toggleActive = async (u) => {
    await authApi.patch(`/users/${u.id}`, { is_active: !u.is_active });
    showToast(u.is_active ? `${u.name} deactivated` : `${u.name} activated`);
    load();
  };

  const changeRole = async (u, role) => {
    await authApi.patch(`/users/${u.id}`, { role });
    showToast(`${u.name}'s role updated to ${role}`);
    load();
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete ${u.name}? This cannot be undone.`)) return;
    await authApi.delete(`/users/${u.id}`);
    showToast(`${u.name} deleted`);
    load();
  };

  const doResetPw = async () => {
    if (!newPw || newPw.length < 6) { showToast('Min 6 characters','error'); return; }
    await authApi.post(`/users/${resetPw.id}/reset-password`, { password: newPw });
    showToast(`✓ Password reset for ${resetPw.name}`);
    setResetPw(null); setNewPw('');
  };

  if (loading) return <div className="loading-center"><div className="spinner spinner-lg"/></div>;

  return (
    <>
      {showCreate && (
        <CreateUserModal
          onClose={()=>setCreate(false)}
          onCreated={()=>{ setCreate(false); load(); }}
          showToast={showToast}/>
      )}

      {/* Reset password modal */}
      {resetPw && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setResetPw(null)}>
          <div className="modal-box" style={{width:380}}>
            <div className="modal-header">
              <span className="modal-title">Reset password — {resetPw.name}</span>
              <button className="btn btn-ghost btn-icon" onClick={()=>setResetPw(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">New password</label>
                <input className="form-input" type="password" value={newPw}
                  onChange={e=>setNewPw(e.target.value)} placeholder="Min. 6 characters" autoFocus/>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={doResetPw}>Reset password</button>
              <button className="btn" onClick={()=>setResetPw(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:16}}>
        {ROLES.map(r=>(
          <div key={r.id} className="metric-card" style={{marginBottom:0}}>
            <div className="metric-label">{r.label}s</div>
            <div className="metric-value" style={{color:r.color}}>
              {users.filter(u=>u.role===r.id).length}
            </div>
          </div>
        ))}
      </div>

      {/* Header */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
        <div style={{fontSize:13,color:'var(--text-2)'}}>
          {users.length} user{users.length!==1?'s':''} total ·{' '}
          {users.filter(u=>u.is_active).length} active
        </div>
        <button className="btn btn-primary btn-sm" onClick={()=>setCreate(true)}>
          + Add user
        </button>
      </div>

      {/* Users table */}
      <div className="card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Gmail connected</th>
                <th>Last login</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} style={{opacity:u.is_active?1:.55}}>
                  <td>
                    <div style={{display:'flex',alignItems:'center',gap:10}}>
                      <div className="avatar av-sm av-blue" style={{flexShrink:0}}>
                        {u.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div style={{fontWeight:600,fontSize:13}}>
                          {u.name}
                          {u.id===me?.id && <span style={{fontSize:10,color:'var(--text-2)',marginLeft:5}}>(you)</span>}
                        </div>
                        <div style={{fontSize:11,color:'var(--text-2)'}}>{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    {u.id === me?.id ? (
                      <RoleBadge role={u.role}/>
                    ) : (
                      <select value={u.role} onChange={e=>changeRole(u,e.target.value)}
                        style={{fontSize:12,border:'1px solid var(--border)',borderRadius:6,
                          padding:'3px 8px',background:'var(--surface)',cursor:'pointer'}}>
                        {ROLES.map(r=><option key={r.id} value={r.id}>{r.label}</option>)}
                      </select>
                    )}
                  </td>
                  <td>
                    {u.gmail_email
                      ? <span style={{fontSize:12,color:'var(--green)'}}>✓ {u.gmail_email}</span>
                      : <span style={{fontSize:12,color:'var(--text-3)'}}>Not connected</span>}
                  </td>
                  <td style={{fontSize:12,color:'var(--text-2)'}}>
                    {u.last_login
                      ? new Date(u.last_login).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})
                      : 'Never'}
                  </td>
                  <td>
                    <span className={`pill ${u.is_active?'pill-green':'pill-gray'}`} style={{fontSize:10}}>
                      {u.is_active?'Active':'Inactive'}
                    </span>
                  </td>
                  <td onClick={e=>e.stopPropagation()}>
                    <div style={{display:'flex',gap:5}}>
                      {u.id !== me?.id && (
                        <>
                          <button className="btn btn-sm"
                            title={u.is_active?'Deactivate':'Activate'}
                            onClick={()=>toggleActive(u)}>
                            {u.is_active?'⏸':'▶'}
                          </button>
                          <button className="btn btn-sm" title="Reset password"
                            onClick={()=>{ setResetPw(u); setNewPw(''); }}>
                            🔑
                          </button>
                          <button className="btn btn-danger btn-sm"
                            title="Delete user" onClick={()=>deleteUser(u)}>✕</button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Role permissions reference */}
      <div className="card">
        <div className="card-header"><span className="card-title">Role permissions reference</span></div>
        <div className="card-body">
          <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12}}>
            {ROLES.map(r => (
              <div key={r.id} style={{border:`1px solid ${r.color}44`,borderRadius:'var(--r)',
                overflow:'hidden'}}>
                <div style={{padding:'8px 12px',background:r.bg,borderBottom:`1px solid ${r.color}33`}}>
                  <span style={{fontWeight:600,fontSize:13,color:r.color}}>{r.label}</span>
                </div>
                <div style={{padding:'8px 12px',display:'flex',flexDirection:'column',gap:4}}>
                  {['leads','contacts','email','replies','campaigns','pipeline','analytics','settings','users','generate_leads'].map(p=>{
                      const has = (ROLE_PERMS[r.id]||[]).includes(p);
                      return (
                        <div key={p} style={{display:'flex',alignItems:'center',gap:6,fontSize:12,
                          color:has?'var(--text)':'var(--text-3)'}}>
                          <span style={{color:has?'var(--green)':'var(--text-3)',fontSize:11}}>
                            {has?'✓':'✗'}
                          </span>
                          {PERM_ICONS[p]} {p.replace('_',' ')}
                        </div>
                      );
                    })}
                  </div>
                </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
